import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const featuresGridSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  columns: z.enum(['2', '3', '4']).default('3'),
  align: z.enum(['left', 'center']).default('left'),
  theme: z.enum(['light', 'dark']).default('light'),
  features: z
    .array(
      z.object({
        icon: z.string().max(40).optional(),
        title: z.string().min(1).max(160),
        description: z.string().max(500),
      }),
    )
    .min(1)
    .max(12),
});

export type FeaturesGridData = z.infer<typeof featuresGridSchema>;

export function FeaturesGridRenderer({
  eyebrow,
  title,
  subtitle,
  columns = '3',
  align = 'left',
  theme = 'light',
  features,
}: FeaturesGridData) {
  const safeFeatures = Array.isArray(features) ? features : [];
  const wrapperCls = `${theme === 'light' ? 'v5-light' : ''} v5-section v5-features-section`.trim();
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className={wrapperCls}>
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-feat-h2">{title}</h2>
          {subtitle ? <p className="v5-feat-sub">{subtitle}</p> : null}
        </div>
        <div className={`v5-feat-grid v5-feat-grid-${columns}`}>
          {safeFeatures.map((f, i) => (
            <article key={i} className="v5-feat-card">
              {f.icon ? <div className="v5-feat-icon">{f.icon}</div> : null}
              <h3 className="v5-feat-title">{f.title}</h3>
              <p className="v5-feat-desc">{f.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export const featuresGridBlock: BlockDefinition<FeaturesGridData> = {
  meta: {
    type: 'features-grid',
    label: 'Features grid (v5)',
    description: 'Grid de 2/3/4 columnas con tarjetas (icono + título + descripción). Estilo v5.',
    category: 'content',
    icon: 'grid-3x3',
  },
  schema: featuresGridSchema,
  defaults: {
    eyebrow: '● Por qué con nosotros',
    title: '6 razones por las que los clientes nos eligen',
    subtitle: 'Detrás de cada operación hay años de esfuerzo y una historia que merece respeto.',
    columns: '3',
    align: 'left',
    theme: 'light',
    features: [
      { icon: '🛡', title: 'Confidencialidad como estándar', description: 'Cada operación arranca con NDA. Contactamos sin revelar el nombre.' },
      { icon: '🎯', title: 'Solo mandatos viables', description: 'Si no se puede vender bien, te lo decimos antes de firmar.' },
      { icon: '📊', title: 'Valoración profesional', description: 'Sales con un precio defendible, no con un capricho.' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'columns',
      label: 'Columnas',
      type: 'select',
      options: [
        { label: '2 columnas', value: '2' },
        { label: '3 columnas', value: '3' },
        { label: '4 columnas', value: '4' },
      ],
    },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'theme',
      label: 'Tema',
      type: 'select',
      options: [
        { label: 'Claro (sobre fondo blanco)', value: 'light' },
        { label: 'Oscuro (sobre fondo oscuro)', value: 'dark' },
      ],
    },
    {
      name: 'features',
      label: 'Tarjetas',
      type: 'array',
      max: 12,
      itemFields: [
        { name: 'icon', label: 'Icono (emoji)', type: 'text', max: 40 },
        { name: 'title', label: 'Título', type: 'text', required: true, max: 160 },
        { name: 'description', label: 'Descripción', type: 'textarea', required: true, max: 500 },
      ],
    },
  ],
  Renderer: FeaturesGridRenderer,
};
